import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { financialsApi } from '@/lib/financialsApi';
import { toNumber } from '@/lib/number';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  BarChart3,
  ChevronRight,
  Wallet,
} from 'lucide-react';
import type { ApiResponse } from '@/types/api';
import type { FarmFinancialSummary, CycleSummaryItem } from '@/types/financials';

export const Route = createFileRoute('/_app/financials/')({
  component: FinancialsOverview,
});

function formatPHP(value: number): string {
  return value.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' });
}

function FinancialsOverview() {
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();

  const { data, isLoading, error, refetch } = useQuery<ApiResponse<FarmFinancialSummary>>({
    queryKey: ['financials', 'summary', farmId],
    queryFn: () => financialsApi.getFarmSummary(farmId!),
    staleTime: 60 * 1000,
    enabled: !!farmId,
  });

  const summary = data?.data;

  if (isFarmLoading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading financial data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load financial data'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!summary) {
    return (
      <Card>
        <Card.Body className="text-center py-12">
          <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-900">No financial data yet</h3>
          <p className="text-sm text-gray-500 mt-1">
            Start adding expenses or complete cycles to see financial summaries.
          </p>
        </Card.Body>
      </Card>
    );
  }

  const totalCosts = toNumber(summary.total_costs);
  const totalRevenue = toNumber(summary.total_revenue);
  const totalProfit = toNumber(summary.total_profit);
  const avgCostPerKg = summary.avg_cost_per_kg != null ? toNumber(summary.avg_cost_per_kg) : null;
  const avgRoi = summary.avg_roi_pct;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <SummaryCards
        totalCosts={totalCosts}
        totalRevenue={totalRevenue}
        totalProfit={totalProfit}
        avgCostPerKg={avgCostPerKg}
        avgRoi={avgRoi}
        activeCycles={summary.active_cycles}
        harvestedCycles={summary.harvested_cycles}
      />

      {/* Cycle performance table */}
      {summary.by_cycle.length > 0 && (
        <Card>
          <Card.Header
            action={
              <Link
                to="/financials/comparison"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Compare cycles
              </Link>
            }
          >
            Cycle Performance
          </Card.Header>
          <CycleTable cycles={summary.by_cycle} />
        </Card>
      )}

      {/* Cost breakdown */}
      {Object.keys(summary.cost_breakdown).length > 0 && (
        <Card>
          <Card.Header>Cost Breakdown by Category</Card.Header>
          <Card.Body>
            <CostBreakdownBars breakdown={summary.cost_breakdown} total={totalCosts} />
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Summary cards
// ============================================================================

interface SummaryCardsProps {
  totalCosts: number;
  totalRevenue: number;
  totalProfit: number;
  avgCostPerKg: number | null;
  avgRoi: number | null;
  activeCycles: number;
  harvestedCycles: number;
}

function SummaryCards({
  totalCosts,
  totalRevenue,
  totalProfit,
  avgCostPerKg,
  avgRoi,
  activeCycles,
  harvestedCycles,
}: SummaryCardsProps) {
  const cards = [
    {
      label: 'Total Costs',
      value: formatPHP(totalCosts),
      icon: DollarSign,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
    },
    {
      label: 'Total Revenue',
      value: formatPHP(totalRevenue),
      icon: TrendingUp,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      label: 'Net Profit',
      value: formatPHP(totalProfit),
      icon: totalProfit >= 0 ? TrendingUp : TrendingDown,
      iconBg: totalProfit >= 0 ? 'bg-emerald-50' : 'bg-red-50',
      iconColor: totalProfit >= 0 ? 'text-emerald-600' : 'text-red-600',
      valueColor: totalProfit >= 0 ? 'text-emerald-700' : 'text-red-700',
    },
    {
      label: 'Avg Cost/kg',
      value: avgCostPerKg != null ? formatPHP(avgCostPerKg) : '--',
      icon: BarChart3,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => (
          <Card key={card.label}>
            <Card.Body className="flex items-center gap-4">
              <div className={`rounded-lg p-2.5 ${card.iconBg}`}>
                <card.icon className={`h-5 w-5 ${card.iconColor}`} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{card.label}</p>
                <p className={`text-xl font-semibold ${'valueColor' in card && card.valueColor ? card.valueColor : 'text-gray-900'}`}>
                  {card.value}
                </p>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>

      {/* Secondary info row */}
      <div className="flex items-center gap-4 text-sm text-gray-500">
        <span>
          <strong className="text-gray-700">{activeCycles}</strong> active{' '}
          {activeCycles === 1 ? 'cycle' : 'cycles'}
        </span>
        <span className="text-gray-300">|</span>
        <span>
          <strong className="text-gray-700">{harvestedCycles}</strong> harvested
        </span>
        {avgRoi != null && (
          <>
            <span className="text-gray-300">|</span>
            <span>
              Avg ROI:{' '}
              <strong className={avgRoi >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                {avgRoi.toFixed(1)}%
              </strong>
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Cycle performance table
// ============================================================================

function CycleTable({ cycles }: { cycles: CycleSummaryItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 text-left text-gray-500">
            <th className="px-5 py-3 font-medium">Cycle</th>
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium text-right">Cost</th>
            <th className="px-5 py-3 font-medium text-right">Revenue</th>
            <th className="px-5 py-3 font-medium text-right">Profit</th>
            <th className="px-5 py-3 font-medium text-right">Harvest (kg)</th>
            <th className="px-5 py-3 font-medium text-right">Cost/kg</th>
            <th className="px-5 py-3 font-medium text-right">ROI</th>
            <th className="px-5 py-3 font-medium" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {cycles.map((cycle) => {
            const cost = toNumber(cycle.total_cost);
            const revenue = toNumber(cycle.total_revenue);
            const profit = toNumber(cycle.profit);
            const harvestKg = toNumber(cycle.harvest_kg);
            const costPerKg = cycle.cost_per_kg != null ? toNumber(cycle.cost_per_kg) : null;

            return (
              <tr key={cycle.cycle_id} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-5 py-3">
                  <div>
                    <span className="font-medium text-gray-900">
                      {cycle.cycle_name || 'Unnamed'}
                    </span>
                    {cycle.pond_name && (
                      <span className="ml-1 text-xs text-gray-400">({cycle.pond_name})</span>
                    )}
                  </div>
                  {cycle.stocking_date && (
                    <span className="text-xs text-gray-400">
                      {new Date(cycle.stocking_date).toLocaleDateString()}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <StatusBadge status={cycle.status} />
                </td>
                <td className="px-5 py-3 text-right text-gray-600">
                  {formatPHP(cost)}
                </td>
                <td className="px-5 py-3 text-right text-gray-600">
                  {formatPHP(revenue)}
                </td>
                <td className="px-5 py-3 text-right">
                  <span className={profit >= 0 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                    {formatPHP(profit)}
                  </span>
                </td>
                <td className="px-5 py-3 text-right text-gray-600">
                  {harvestKg > 0 ? harvestKg.toLocaleString() : '--'}
                </td>
                <td className="px-5 py-3 text-right text-gray-600">
                  {costPerKg != null ? formatPHP(costPerKg) : '--'}
                </td>
                <td className="px-5 py-3 text-right">
                  {cycle.roi_pct != null ? (
                    <span className={cycle.roi_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {cycle.roi_pct.toFixed(1)}%
                    </span>
                  ) : (
                    '--'
                  )}
                </td>
                <td className="px-5 py-3">
                  <Link
                    to="/financials/cycles/$cycleId"
                    params={{ cycleId: cycle.cycle_id }}
                    className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors inline-flex"
                    title="View details"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="neutral">Unknown</Badge>;

  switch (status) {
    case 'active':
      return <Badge variant="ok" dot>Active</Badge>;
    case 'completed':
    case 'harvested':
      return <Badge variant="success" dot>Completed</Badge>;
    case 'cancelled':
      return <Badge variant="critical" dot>Cancelled</Badge>;
    default:
      return <Badge variant="neutral">{status}</Badge>;
  }
}

// ============================================================================
// Cost breakdown bars
// ============================================================================

const CATEGORY_COLORS: Record<string, string> = {
  feed: 'bg-blue-500',
  probiotics: 'bg-teal-500',
  chemicals: 'bg-purple-500',
  labor: 'bg-orange-500',
  energy: 'bg-yellow-500',
  seed: 'bg-pink-500',
  equipment: 'bg-indigo-500',
  transport: 'bg-cyan-500',
  other: 'bg-gray-400',
};

function getCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] || 'bg-gray-400';
}

function CostBreakdownBars({
  breakdown,
  total,
}: {
  breakdown: Record<string, number | string>;
  total: number;
}) {
  const entries = Object.entries(breakdown)
    .map(([key, val]) => ({ category: key, amount: toNumber(val) }))
    .sort((a, b) => b.amount - a.amount);

  if (entries.length === 0 || total === 0) {
    return <p className="text-sm text-gray-500">No cost data available.</p>;
  }

  return (
    <div className="space-y-3">
      {entries.map((entry) => {
        const pct = total > 0 ? (entry.amount / total) * 100 : 0;
        return (
          <div key={entry.category}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-700 capitalize">{entry.category}</span>
              <span className="text-gray-500">
                {formatPHP(entry.amount)} ({pct.toFixed(1)}%)
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getCategoryColor(entry.category)}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
